import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import MultiCheckBox from '@cont/StoreListing/MultiCheckBox';
import RadioButton from '@cont/StoreListing/RadioButton';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import useGoalEventProps from '@hooks/useGoalEventProps';
import {
  ALL,
  SINGLE_SELECT,
  STORE_TAB_FILTER_APPLIED,
  STORE_TAB_FILTER_CLICKED
} from './constants';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubHeadingMobile = styled(Typography)``;

const BottomSheetWrap = styled.div<{ $height: number }>`
  display: flex;
  border-top: ${Spacings.SPACING_1} solid var(--color-i-divider);
  max-height: calc(100vh - 232px);
  height: ${({ $height }) => $height}px;
  min-height: calc(100vh - 414px);
`;

const ButtonWrapper = styled.div`
  width: 100%;
  align-items: center;
  background: white;
  height: ${Spacings.SPACING_20B};
  & .css-1fw6f0o-ClearSelectionButton {
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_1B};
  }
  display: flex;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  z-index: 1;
  padding: ${Spacings.SPACING_4B};
  border-top: ${Spacings.SPACING_1} solid var(--color-i-divider);
`;

const SelectionButton = styled(AquillaButton)`
  width: 156px;
  height: ${Spacings.SPACING_12B};
  position: relative;
`;

const NavBar = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 36%;
  background: var(--color-base-light-0);
  border-right: ${Spacings.SPACING_1} solid var(--color-i-divider);
  flex: 1;
  overflow: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Edge */
  }
`;

const NavItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_3B} ${Spacings.SPACING_3B}
    ${Spacings.SPACING_4B};
  cursor: pointer;
  :hover {
    background-color: var(--color-base-1);
  }
  background-color: ${({ $isSelected }) =>
    $isSelected ? 'var(--color-base-1)' : ''};
  border: ${({ $isSelected }) =>
    $isSelected ? `${Spacings.SPACING_1} solid var(--color-i-divider)` : ''};
  border-right: 0px;
  :first-child {
    border-top: 0px;
  }
  & .aquilla-typography {
    font-weight: ${({ $isSelected }) =>
      $isSelected ? `${Fonts.SEMIBOLD}` : ''};
  }
  gap: ${Spacings.SPACING_3B};
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 64%;
  height: 100%;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B} ${Spacings.SPACING_6B}
    ${Spacings.SPACING_4B};
  flex: 1;
  overflow: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Edge */
  }
`;

const Header = styled.div`
  height: 104px;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  display: flex;
  align-items: flex-end;
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--color-base-1);
`;

const Dot = styled.div`
  display: flex;
  height: ${Spacings.SPACING_1B};
  width: ${Spacings.SPACING_1B};
  background: var(--color-i-blue);
  border-radius: ${Spacings.SPACING_2};
`;

const FilterHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
`;

const ClearAll = styled.div`
  text-align: center;
  transition: 0.3s ease;
  text-decoration: underline;
  margin-left: ${Spacings.SPACING_4B};
  cursor: pointer;
  &:hover {
    transform: translateY(-1px);
  }
  & .aquilla-typography {
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const FilterBottomSheet = ({
  bottomSheetWrapRef,
  openSelectedFilter,
  filters,
  filterObj,
  onChange,
  onClear,
  resetFilters,
  onApply,
  getAllOptionsByFilterName
}) => {
  const goalUID = useRouter().query.goalUID as string;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const [openFilter, setOpenFilter] = useState(
    Object.keys(openSelectedFilter).length !== 0
      ? openSelectedFilter
      : filters[0]
  );
  const handleNavClick = (filter) => {
    setOpenFilter(filter);
    segment.track(STORE_TAB_FILTER_CLICKED, {
      ...goalSegmentData,
      filter_type: filter.label
    });
  };
  const allOptions = getAllOptionsByFilterName(openFilter.key);
  const hasAllOption = allOptions.find((option) => {
    return option === ALL;
  });
  const hasSelectedValues = filterObj[openFilter.key].length;

  const [bottomSheetHeight, setBottomSheetHeight] = useState(0);

  const handleReset = () => {
    segment.track(STORE_TAB_FILTER_APPLIED, {
      ...goalSegmentData,
      filter_type: [],
      filter_value: [],
      filter_type_count: 0
    });
    resetFilters();
  };

  const calculateHeight = () => {
    const filterConstant = 14;
    const optionContsant = 12 + 24 + 56 + 14 + 14; // all the extra margins and paddings excluding the option height;
    // TODO: Make all these margins/paddings constant and pass them in the styled components

    const filterHeight = filters.length * 44 + filterConstant;

    const optionsHeight =
      filters.reduce((maxLength, filter) => {
        if (Array.isArray(filter?.values)) {
          const optionsLength = filter?.values?.length;
          return optionsLength > maxLength ? optionsLength : maxLength;
        }
        return maxLength;
      }, 0) *
        48 +
      optionContsant;

    setBottomSheetHeight(Math.max(filterHeight, optionsHeight));
  };

  useEffect(() => {
    calculateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper ref={bottomSheetWrapRef}>
      <Header>
        <Typography variant="h3">Filters</Typography>
      </Header>
      <BottomSheetWrap $height={bottomSheetHeight}>
        <NavBar>
          {filters.map((filter) => {
            return (
              <NavItem
                $isSelected={filter.key === openFilter.key}
                onClick={() => handleNavClick(filter)}
                key={filter.key}
              >
                <Typography variant="p2">{filter.label}</Typography>
                {filterObj[filter.key].length > 0 &&
                  filterObj[filter.key]?.[0] !==
                    filter?.defaultSelection?.code && <Dot />}
              </NavItem>
            );
          })}
        </NavBar>
        <FilterWrapper>
          <FilterHeading>
            <SubHeadingMobile variant="h5">{openFilter.label}</SubHeadingMobile>
            {hasAllOption && hasSelectedValues > 1 && (
              <ClearAll onClick={() => onClear(openFilter)}>
                <Typography variant="p3">Clear all</Typography>
              </ClearAll>
            )}
          </FilterHeading>
          {openFilter.selectType === SINGLE_SELECT ? (
            <RadioButton
              options={openFilter.values}
              filterName={openFilter.key}
              filterType={openFilter.selectType}
              selected={filterObj[openFilter.key]}
              onChange={onChange}
            />
          ) : (
            <MultiCheckBox
              options={openFilter.values}
              filterType={openFilter.selectType}
              selected={filterObj[openFilter.key]}
              filterName={openFilter.key}
              onChange={onChange}
            />
          )}
        </FilterWrapper>
      </BottomSheetWrap>
      <ButtonWrapper>
        <SelectionButton variant="secondary" onClick={handleReset}>
          Reset filters
        </SelectionButton>
        <SelectionButton variant="primary" onClick={onApply}>
          Apply
        </SelectionButton>
      </ButtonWrapper>
    </Wrapper>
  );
};

export default FilterBottomSheet;
